/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent } from 'react';

import PropTypes from 'prop-types';

import {
    IDLE_GRID_GUIDE_DURATION,
    MAX_GRID_GUIDE_PROGRAM_CELL_LENGTH,
    MILLISECONDS_IN_A_MINUTE,
    MILLISECONDS_IN_A_SECOND,
} from '../../../config';
import { AppUtils } from '../../../core/AppUtils';
import { DeviceFeatures } from '../../../core/device';
import { Channel } from '../../../core/entities/Channel';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { TChannelV2, TGridGuideRow, TGuideCell } from '../../../core/types';
import { DateTime } from '../../../core/utils';
import { ChannelCell } from '../ChannelCell';
import { GuideHighlight } from '../GuideHighlight';
import { GuideProps } from '../GuideProps';
import { ProgramCell } from '../ProgramCell';
import { CellTypes } from '../ProgramCell/CellTypes';
import { Row } from '../Row';

const MainGuide: FunctionComponent<GuideProps> = ({
    limit: LIMIT,
    channels,
    guideRows,
    currentTimestamp,
    nextTimestamp,
    visibilityState,
    hideMetadata,
    recordingsStatusMap,
    isGuideRowsServiceFail,
}): JSX.Element => {
    const createGuideWithNoInformation = () => {
        return new Array(LIMIT).fill(0).map((_, index) => {
            return createRow(index);
        });
    };

    const createGuideWithChannelInformation = (aChannels: TChannelV2[] | Channel[]) => {
        if (!aChannels || aChannels.length === 0) return null;
        const channelRowNumber = Math.min(LIMIT, aChannels.length);

        return new Array(channelRowNumber).fill(0).map((_, index) => {
            return createRow(index, aChannels);
        });
    };

    const createGuideWithChannelAndGuideRows = (
        aChannels: TChannelV2[] | Channel[],
        aGuideRows: TGridGuideRow[],
    ) => {
        if (!aChannels || aChannels.length === 0) return null;
        if (!aGuideRows || aGuideRows.length === 0) return null;

        return new Array(LIMIT).fill(0).map((_, index) => {
            return (
                aGuideRows[index] && (
                    <Row key={index}>
                        {createTrailingProgramCell(aGuideRows[index]) || (
                            <ProgramCell type={CellTypes.TRAILING} />
                        )}
                        <ChannelCell
                            channel={aChannels[index]}
                            highlightId={
                                aChannels[index] ? AppUtils.createHighlightId(0, index) : ''
                            }
                        >
                            {aChannels[index] && (
                                <GuideHighlight id={AppUtils.createHighlightId(0, index)} />
                            )}
                        </ChannelCell>
                        {createGuideProgramCells(aGuideRows[index], aChannels[index]) || (
                            <ProgramCell
                                type={CellTypes.PAST_CONTINUOUS}
                                duration={MAX_GRID_GUIDE_PROGRAM_CELL_LENGTH}
                            />
                        )}
                        {createLeadingProgramCell(aGuideRows[index])}
                    </Row>
                )
            );
        });
    };

    const createTrailingProgramCell = (aGuideRow: TGridGuideRow) => {
        if (!aGuideRow) return null;

        const { guideCells } = aGuideRow;
        if (!guideCells || (guideCells && guideCells.length) === 0) return null;

        const firstCell = guideCells[0];
        const { isAdult, title } = firstCell;
        const cellTitle =
            isAdult && hideMetadata ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN) : title;
        return (
            <ProgramCell
                type={CellTypes.TRAILING}
                title={!isGuideRowsServiceFail ? cellTitle : ''}
            />
        );
    };

    const createLeadingProgramCell = (aGuideRow: TGridGuideRow) => {
        if (!aGuideRow) return null;

        const { guideCells } = aGuideRow;
        if (!guideCells || (guideCells && guideCells.length) === 0) return null;

        const lastCell: TGuideCell = guideCells[guideCells.length - 1];
        const { startTime, duration, title, isAdult } = lastCell;
        const endTime: DateTime = startTime.addSeconds(duration);
        const isFutureContinuous: boolean =
            startTime.getTime() < nextTimestamp.getTime() &&
            endTime.getTime() > nextTimestamp.getTime();
        if (isFutureContinuous) return null;

        const cellTitle =
            isAdult && hideMetadata ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN) : title;
        return (
            <ProgramCell
                type={CellTypes.LEADING}
                title={!isGuideRowsServiceFail ? cellTitle : ''}
            />
        );
    };

    let rowCount: number = 0;

    const createGuideProgramCells = (aGuideRow: TGridGuideRow, aChannel: TChannelV2 | Channel) => {
        if (!aGuideRow || !aChannel) return null;

        const { guideCells } = aGuideRow;
        if (!guideCells || (guideCells && guideCells.length) === 0) return null;

        let noMoreCell = false;
        let currLen = 0;
        let counter = 0;
        const isSocuEnabled: boolean = DeviceFeatures.isSocuEnabled();
        const programCells = guideCells.slice(1).map((guideCell) => {
            if (noMoreCell) return null;
            const {
                title,
                duration,
                startTime,
                isAdult,
                isNew,
                movieYear,
                isStartover,
                isCatchup,
                offerId,
            } = guideCell;

            const actualStartTime =
                startTime.getTime() < currentTimestamp.getTime() ? currentTimestamp : startTime;
            const endTime: DateTime = startTime.addSeconds(duration);

            if (
                endTime.getTime() >= nextTimestamp.getTime() ||
                currLen >= IDLE_GRID_GUIDE_DURATION
            ) {
                noMoreCell = true;
            }
            const key = `stationId-${counter}`;
            counter += 1;

            const expectedCellLength =
                (endTime.getTime() - actualStartTime.getTime()) / MILLISECONDS_IN_A_MINUTE;
            const lengthRemaining = IDLE_GRID_GUIDE_DURATION - currLen;
            let actualCellLength =
                expectedCellLength < lengthRemaining ? expectedCellLength : lengthRemaining;
            currLen += expectedCellLength;

            let cellType = CellTypes.DEFAULT;
            let isLeft = false;
            if (startTime.getTime() < currentTimestamp.getTime()) {
                isLeft = true;
                cellType = CellTypes.PAST_CONTINUOUS;
            }

            let isRight = false;
            if (endTime.getTime() > nextTimestamp.getTime()) {
                cellType = CellTypes.FUTURE_CONTINUOUS;
                isRight = true;
                noMoreCell = true;
                actualCellLength += 6;
            }

            const cuAvailabilityDuration =
                (aChannel.policyInfo?.find((policy) => policy?.socuRules)?.socuRules
                    ?.cuAvailabilityDurationSeconds || 0) * MILLISECONDS_IN_A_SECOND;

            const shouldHideMetadata = isAdult && hideMetadata;
            const recordingStatus = recordingsStatusMap.get(`${aChannel.stationId}${offerId}`);

            return (
                <ProgramCell
                    key={key}
                    title={
                        shouldHideMetadata ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN) : title
                    }
                    isNew={shouldHideMetadata ? false : isNew}
                    isLeft={isLeft}
                    isRight={isRight}
                    type={cellType}
                    duration={actualCellLength}
                    highlightId={AppUtils.createHighlightId(counter, rowCount)}
                    isSubscribed={aChannel.isSubscribed}
                    movieYear={!shouldHideMetadata ? movieYear : null}
                    isStartover={isSocuEnabled && isStartover}
                    isCatchup={isSocuEnabled && isCatchup}
                    startTime={startTime}
                    endTime={endTime}
                    cuAvailabilityDuration={cuAvailabilityDuration}
                    // TODO: refactor: move visibility state enum out of store
                    isRefreshed={visibilityState === 'ON_RESUME'}
                    channel={aChannel}
                    programCell={guideCell}
                    recordingStatus={recordingStatus}
                />
            );
        });
        rowCount += 1;
        return programCells;
    };

    const createGuide = (aChannels: TChannelV2[] | Channel[], aGuideRows: TGridGuideRow[]) => {
        if (aGuideRows && aGuideRows.length > 0)
            return createGuideWithChannelAndGuideRows(aChannels, aGuideRows);
        if (aChannels && aChannels.length > 0) return createGuideWithChannelInformation(aChannels);

        return createGuideWithNoInformation();
    };

    const createRow = (index: number, aChannels?: TChannelV2[] | Channel[]) => {
        const highlightId = AppUtils.createHighlightId(0, index);

        return (
            <Row key={index}>
                <ProgramCell type={CellTypes.TRAILING} />
                {!aChannels ? (
                    <ChannelCell highlightId={highlightId}>
                        <GuideHighlight id={highlightId} />
                    </ChannelCell>
                ) : (
                    <ChannelCell channel={aChannels[index]} highlightId={highlightId}>
                        <GuideHighlight id={highlightId} />
                    </ChannelCell>
                )}

                <ProgramCell
                    type={CellTypes.PAST_CONTINUOUS}
                    duration={MAX_GRID_GUIDE_PROGRAM_CELL_LENGTH}
                />
            </Row>
        );
    };

    return <React.Fragment>{createGuide(channels, guideRows)}</React.Fragment>;
};

MainGuide.propTypes = {
    limit: PropTypes.number.isRequired,
    channels: PropTypes.array.isRequired,
    guideRows: PropTypes.any,
    currentTimestamp: PropTypes.any,
    nextTimestamp: PropTypes.any,
    visibilityState: PropTypes.string.isRequired,
};

export default MainGuide;
